import { useState } from "react";
import { Link, Navigate } from "react-router";
import { useForm } from "react-hook-form";
import {
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
} from "firebase/auth";
import { FirebaseError } from "firebase/app";
import { auth } from "@/data/firebase";
import { useAuth } from "@/data/AuthProvider";
import { Navbar } from "@/components/layout/Navbar";
import { PageLoader } from "@/components/ui/PageLoader";

interface Credentials {
  email: string;
  password: string;
}

/** Firebase's raw codes are not user-facing text. */
function readableAuthError(error: unknown): string {
  if (!(error instanceof FirebaseError)) {
    return error instanceof Error ? error.message : "Sign in failed. Please try again.";
  }

  switch (error.code) {
    case "auth/invalid-credential":
    case "auth/wrong-password":
    case "auth/user-not-found":
      return "That email and password combination doesn't match an account.";
    case "auth/invalid-email":
      return "That doesn't look like a valid email address.";
    case "auth/email-already-in-use":
      return "An account already exists for that email. Try signing in instead.";
    case "auth/weak-password":
      return "Please choose a password of at least six characters.";
    case "auth/too-many-requests":
      return "Too many attempts. Wait a moment and try again.";
    case "auth/missing-email":
      return "Enter your email address first.";
    case "auth/popup-closed-by-user":
    case "auth/cancelled-popup-request":
      return "The sign-in window closed before finishing.";
    case "auth/popup-blocked":
      return "Your browser blocked the sign-in pop-up. Allow pop-ups for this site and retry.";
    default:
      return error.message;
  }
}

/**
 * Sign-in and sign-up, hand-rolled.
 *
 * Replaces `react-firebaseui`, which is abandoned and pulled the whole
 * FirebaseUI widget in for what amounts to two providers and a form.
 *
 * Note the react-hook-form v5 -> v7 break: fields register by spreading
 * `{...register(name, rules)}` onto the input. v5 passed a callback ref —
 * `ref={register({ required: true })}` — and exposed errors as `errors` off
 * the hook rather than `formState.errors`.
 */
export function SignIn({ mode = "signIn" }: { mode?: "signIn" | "signUp" }) {
  const isSignUp = mode === "signUp";
  const authState = useAuth();
  const [formError, setFormError] = useState<string | null>(null);
  const [resetSentTo, setResetSentTo] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<Credentials>();

  /**
   * Send a reset link to whatever is already in the email field.
   *
   * Deliberately not a separate screen: the address is nearly always already
   * typed by the time someone realises they cannot remember the password, and
   * a second form asking for it again is a step that exists only to be a step.
   *
   * The response is identical whether or not the account exists. Firebase will
   * happily report `auth/user-not-found`, which turns this into an oracle for
   * checking which email addresses have accounts here.
   */
  async function onForgotPassword() {
    setFormError(null);
    setResetSentTo(null);

    const email = getValues("email")?.trim();
    if (!email) {
      setFormError("Enter your email address first, then choose Forgot your password.");
      return;
    }

    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      // Anything other than a malformed address is swallowed on purpose — see
      // the note above about not confirming which addresses are registered.
      if (error instanceof FirebaseError && error.code === "auth/invalid-email") {
        setFormError(readableAuthError(error));
        return;
      }
    }

    setResetSentTo(email);
  }

  if (authState.status === "loading") return <PageLoader />;
  if (authState.status === "signedIn") return <Navigate to="/trade/list" replace />;

  async function onSubmit({ email, password }: Credentials) {
    setFormError(null);
    try {
      const result = isSignUp
        ? await createUserWithEmailAndPassword(auth, email, password)
        : await signInWithEmailAndPassword(auth, email, password);

      if (isSignUp) {
        // Firebase does not send this on its own — an account created with a
        // password is usable immediately unless something asks for it.
        // Failure here is not fatal: the account exists either way, and the
        // verify screen can resend. Swallowing it to keep the user on a
        // "your account was not created" error would be actively wrong.
        await sendEmailVerification(result.user).catch(() => undefined);
      }

      // Runs on sign-in too, not just sign-up. It is idempotent, and an
      // authenticated user without a profile document is a broken state —
      // reachable if the document is ever deleted out from under them.
      //
      // Gated on verification because the Firestore rules require it: an
      // unverified user's write is rejected, and a rejection here would
      // surface as a sign-in failure for an account that signed in fine.
      // Verifying later routes back through this same function.
      if (result.user.emailVerified) {
        const { ensureAppUser } = await import("@/data/users");
        await ensureAppUser(result.user);
      }
    } catch (error) {
      setFormError(readableAuthError(error));
    }
  }

  async function onGoogle() {
    setFormError(null);
    try {
      const result = await signInWithPopup(auth, new GoogleAuthProvider());
      // Idempotent, so it is safe on every sign-in. The 2020 code inspected
      // `additionalUserInfo.isNewUser` and could skip the profile write.
      // Google verifies the address itself, so this is not gated in practice.
      if (result.user.emailVerified) {
        const { ensureAppUser } = await import("@/data/users");
        await ensureAppUser(result.user);
      }
    } catch (error) {
      setFormError(readableAuthError(error));
    }
  }

  return (
    <div className="flex min-h-screen flex-col">
      <title>{isSignUp ? "Get Started | Trade-r" : "Sign In | Trade-r"}</title>
      <Navbar />

      <main className="flex flex-1 items-center justify-center px-4 py-16">
        <div className="w-full max-w-md">
          <p className="eyebrow">{isSignUp ? "New account" : "Welcome back"}</p>
          <h1 className="mt-1 text-5xl font-black tracking-tighter">
            {isSignUp ? "Get started" : "Sign in"}
          </h1>

          {formError && (
            <p className="notice-error mt-6" role="alert">
              {formError}
            </p>
          )}

          {resetSentTo && (
            <p className="notice-info mt-6" role="status">
              If <strong className="font-mono">{resetSentTo}</strong> has an account, a link to
              set a new password is on its way. It can take a minute — check your spam folder.
            </p>
          )}

          <div className="mt-8 border-2 border-ink bg-paper-raised p-6">
            <button
              type="button"
              className="btn-outline w-full"
              onClick={() => void onGoogle()}
              disabled={isSubmitting}
            >
              Continue with Google
            </button>

            <div className="my-6 flex items-center gap-4 font-mono text-[0.65rem] tracking-[0.2em] text-ink-soft uppercase">
              <span className="h-0.5 flex-1 bg-ink" />
              OR
              <span className="h-0.5 flex-1 bg-ink" />
            </div>

            <form onSubmit={(e) => void handleSubmit(onSubmit)(e)} noValidate className="space-y-4">
              <p className="text-right text-sm font-medium">
                {isSignUp ? (
                  <Link to="/user/sign-in" className="underline">
                    Already have an account? Sign in
                  </Link>
                ) : (
                  <Link to="/user/start" className="underline">
                    New here? Sign up
                  </Link>
                )}
              </p>

              <div>
                <label className="field-label" htmlFor="email">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  className="field-input"
                  placeholder="you@example.com"
                  aria-invalid={Boolean(errors.email)}
                  {...register("email", { required: "Email is required" })}
                />
                {errors.email && <p className="field-error">{errors.email.message}</p>}
              </div>

              <div>
                <label className="field-label" htmlFor="password">
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  autoComplete={isSignUp ? "new-password" : "current-password"}
                  className="field-input"
                  placeholder="At least 6 characters"
                  aria-invalid={Boolean(errors.password)}
                  {...register("password", {
                    required: "Password is required",
                    minLength: { value: 6, message: "Password must be at least 6 characters" },
                  })}
                />
                {errors.password && <p className="field-error">{errors.password.message}</p>}
                {!isSignUp && (
                  <button
                    type="button"
                    className="mt-2 cursor-pointer text-sm underline"
                    onClick={() => void onForgotPassword()}
                    disabled={isSubmitting}
                  >
                    Forgot your password?
                  </button>
                )}
              </div>

              <button type="submit" className="btn-primary w-full" disabled={isSubmitting}>
                {isSubmitting ? "Working…" : isSignUp ? "Create Account" : "Sign In"}
              </button>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
}
